import React, { useState } from 'react';
import { TrashIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { AdderSelector, calculateAdderTotal } from './AdderSelector';
import type { AdderSelection } from './AdderSelector';
import { calculateBasePrice, getMultiplierLabel } from './pricingUtils';
export const GLASS_OPTIONS = ['No', 'Obscure', 'Rain', 'Narrow Reed', 'Gluechip'] as const;
export type GlassType = (typeof GLASS_OPTIONS)[number];
export const OPERATION_OPTIONS = ['XO', 'OX', 'Single Hung', 'Double Hung', 'Picture', 'XOX', 'Arch', 'Eyebrow'] as const;
export type OperationType = (typeof OPERATION_OPTIONS)[number];
export interface WindowData {
  id: string;
  qty: number;
  width: number;
  height: number;
  glass: GlassType;
  operations: OperationType[];
  adders: AdderSelection;
  included: boolean;
  notes: string;
}
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}
function OperationSelector({
  operations,
  onChange,
  index
}: {
  operations: OperationType[];
  onChange: (ops: OperationType[]) => void;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  function handleToggle(op: OperationType) {
    if (operations.includes(op)) {
      onChange(operations.filter(o => o !== op));
    } else {
      onChange([...operations, op]);
    }
  }
  const label = operations.length === 0 ? 'Select' : operations.length === 1 ? operations[0] : `${operations.length} selected`;
  return <div className="relative" data-id="element-338">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors text-gray-700 font-medium min-w-[120px]" data-id="element-339">
        <span className="truncate" data-id="element-340">{label}</span>
        {isOpen ? <ChevronUpIcon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" data-id="element-341" /> : <ChevronDownIcon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" data-id="element-342" />}
      </button>

      {operations.length > 0 && !isOpen && <div className="flex flex-wrap gap-1 mt-1.5" data-id="element-343">
          {operations.map(op => <span key={op} className="inline-block px-1.5 py-0.5 text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded" data-id="element-344">
              {op}
            </span>)}
        </div>}

      {isOpen && <div className="absolute z-20 top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg" data-id="element-345">
          <div className="px-3 py-2 border-b border-gray-100" data-id="element-346">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-347">
              Operation
            </span>
          </div>
          <div className="py-1" data-id="element-348">
            {OPERATION_OPTIONS.map(op => <div key={op} className="px-3 py-1.5 hover:bg-gray-50" data-id="element-349">
                <label className="flex items-center gap-2.5 cursor-pointer" data-id="element-350">
                  <input type="checkbox" checked={operations.includes(op)} onChange={() => handleToggle(op)} className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-0" data-id="element-351" />
                  <span className="text-sm text-gray-800" data-id="element-352">{op}</span>
                </label>
              </div>)}
          </div>
          <div className="px-3 py-2 border-t border-gray-100" data-id="element-353">
            <button type="button" onClick={() => setIsOpen(false)} className="w-full text-center text-sm font-medium text-emerald-700 hover:text-emerald-800 py-1" data-id="element-354">
              Done
            </button>
          </div>
        </div>}
    </div>;
}
interface WindowRowProps {
  window: WindowData;
  index: number;
  productLine: string;
  exteriorColorCost: number;
  onUpdate: (id: string, updates: Partial<WindowData>) => void;
  onRemove: (id: string) => void;
  canRemove: boolean;
}
export function WindowRow({
  window: win,
  index,
  productLine,
  exteriorColorCost,
  onUpdate,
  onRemove,
  canRemove
}: WindowRowProps) {
  const sqIn = win.width * win.height;
  const sqFt = sqIn / 144;
  const basePrice = calculateBasePrice(win.width, win.height, productLine);
  const adderCost = calculateAdderTotal(win.adders) + exteriorColorCost;
  const unitPrice = basePrice + adderCost;
  const lineTotal = unitPrice * win.qty;
  const isEven = index % 2 === 0;
  const multiplierLabel = getMultiplierLabel(sqIn, productLine);
  const dimmed = !win.included;
  return <tr className={`${isEven ? 'bg-white' : 'bg-gray-50/70'} ${dimmed ? 'opacity-40' : ''}`} data-id="element-355">
      <td className="px-2 py-3 text-center" data-id="element-356">
        <input type="checkbox" checked={win.included} onChange={() => onUpdate(win.id, {
        included: !win.included
      })} aria-label={`Include window ${index + 1} in quote`} className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-0" data-id="element-357" />
      </td>
      <td className="px-4 py-3 text-sm font-medium text-gray-600 text-center" data-id="element-358">
        {index + 1}
      </td>
      <td className="px-3 py-3" data-id="element-359">
        <input type="number" min={1} value={win.qty || ''} onChange={e => onUpdate(win.id, {
        qty: Math.max(1, Number(e.target.value))
      })} placeholder="1" aria-label={`Window ${index + 1} quantity`} className="w-16 px-2.5 py-1.5 text-sm text-right border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 tabular-nums bg-white" data-id="element-360" />
      </td>
      <td className="px-3 py-3" data-id="element-361">
        <input type="number" min={0} value={win.width || ''} onChange={e => onUpdate(win.id, {
        width: Number(e.target.value)
      })} placeholder="0" aria-label={`Window ${index + 1} width in inches`} className="w-20 px-2.5 py-1.5 text-sm text-right border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 tabular-nums bg-white" data-id="element-362" />
      </td>
      <td className="px-3 py-3" data-id="element-363">
        <input type="number" min={0} value={win.height || ''} onChange={e => onUpdate(win.id, {
        height: Number(e.target.value)
      })} placeholder="0" aria-label={`Window ${index + 1} height in inches`} className="w-20 px-2.5 py-1.5 text-sm text-right border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 tabular-nums bg-white" data-id="element-364" />
      </td>
      <td className="px-4 py-3 text-right" data-id="element-365">
        <span className="text-sm tabular-nums text-gray-700" data-id="element-366">
          {sqFt > 0 ? sqFt.toFixed(1) : '—'}
        </span>
        {multiplierLabel && <span className="block text-[11px] text-gray-400 tabular-nums" data-id="element-367">
            {multiplierLabel}
          </span>}
      </td>
      <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-700" data-id="element-368">
        {basePrice > 0 ? formatCurrency(basePrice) : '—'}
      </td>
      <td className="px-3 py-3" data-id="element-369">
        <OperationSelector operations={win.operations} onChange={ops => onUpdate(win.id, {
        operations: ops
      })} index={index} data-id="element-370" />
      </td>
      <td className="px-3 py-3" data-id="element-371">
        <select value={win.glass} onChange={e => onUpdate(win.id, {
        glass: e.target.value as GlassType
      })} aria-label={`Window ${index + 1} glass obscurity`} className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 min-w-[110px]" data-id="element-372">
          {GLASS_OPTIONS.map(opt => <option key={opt} value={opt} data-id="element-373">
              {opt}
            </option>)}
        </select>
      </td>
      <td className="px-3 py-3" data-id="element-374">
        <AdderSelector adders={win.adders} onChange={adders => onUpdate(win.id, {
        adders
      })} data-id="element-375" />
      </td>
      <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-700" data-id="element-376">
        {adderCost > 0 ? formatCurrency(adderCost) : '—'}
      </td>
      <td className="px-4 py-3 text-sm text-right tabular-nums font-semibold text-gray-900" data-id="element-377">
        {lineTotal > 0 ? formatCurrency(lineTotal) : '—'}
      </td>
      <td className="px-3 py-3" data-id="element-378">
        <input type="text" value={win.notes} onChange={e => onUpdate(win.id, {
        notes: e.target.value
      })} placeholder="Room / notes" aria-label={`Window ${index + 1} notes`} className="w-28 px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-white" data-id="element-379" />
      </td>
      <td className="px-3 py-3 text-center" data-id="element-380">
        {canRemove && <button type="button" onClick={() => onRemove(win.id)} aria-label={`Remove window ${index + 1}`} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors" data-id="element-381">
            <TrashIcon className="w-4 h-4" data-id="element-382" />
          </button>}
      </td>
    </tr>;
}